import { AdminActionBar, AdminListPage, AdminSummaryGrid } from "@zzk/admin";
import { Badge, Pagination, SegmentedControl } from "@zzk/ui";
import type { OperationalSignalRecord } from "../repository.js";
import type { OperationalOverview } from "../service.js";

export function createOperationalOverviewView(overview: OperationalOverview) {
  return AdminListPage({
    title: "Operational overview",
    description:
      "Cloudflare-native signals for frontend, queue, document, job, and module failures.",
    rows: overview.recentSignals,
    columns: [
      {
        id: "status",
        header: "Status",
        cell: (record) =>
          Badge({
            tone: statusTone(record.status),
            children: record.status,
          }),
      },
      {
        id: "category",
        header: "Category",
        cell: (record) =>
          Badge({
            tone: categoryTone(record.category),
            children: record.category,
          }),
      },
      { id: "message", header: "Message", cell: "message" },
      { id: "source", header: "Source", cell: "source" },
      {
        id: "route",
        header: "Route",
        cell: (record) => record.route ?? record.moduleId ?? "unknown",
      },
      {
        id: "queueName",
        header: "Queue",
        cell: (record) => record.queueName ?? "n/a",
      },
      { id: "receivedAt", header: "Received", cell: "receivedAt" },
    ],
    getRowId: (record) => record.id,
    empty: "No operational signals recorded.",
    summary: AdminSummaryGrid({
      items: [
        {
          id: "status",
          label: "Status",
          value: overview.status,
          tone:
            overview.status === "blocked"
              ? "danger"
              : statusTone(overview.status),
        },
        {
          id: "blocked",
          label: "Blocked",
          value: overview.totals.blockedSignals,
          tone: "danger",
          format: "number",
        },
        {
          id: "degraded",
          label: "Degraded",
          value: overview.totals.degradedSignals,
          tone: "warning",
          format: "number",
        },
        {
          id: "frontend",
          label: "Erros frontend",
          value: overview.totals.clientErrors,
          tone: overview.totals.fatalClientErrors > 0 ? "danger" : "info",
          format: "number",
        },
        {
          id: "documents",
          label: "Documentos",
          value: overview.totals.documentSignals,
          tone: "info",
          format: "number",
        },
      ],
    }),
    toolbar: AdminActionBar({
      title: "Janela operacional",
      description:
        overview.operatorHints[0] ?? "No operational hint available.",
      filters: SegmentedControl({
        name: "operational-status",
        value: overview.status,
        options: [
          { value: "healthy", label: "Healthy" },
          { value: "degraded", label: "Degraded" },
          { value: "blocked", label: "Blocked" },
        ],
      }),
    }),
    pagination: Pagination({
      page: 1,
      pageSize: 20,
      totalItems: overview.recentSignals.length,
    }),
  });
}

function statusTone(
  status: OperationalSignalRecord["status"] | OperationalOverview["status"],
) {
  if (status === "blocked") return "danger";
  if (status === "degraded") return "warning";
  return "success";
}

function categoryTone(category: OperationalSignalRecord["category"]) {
  if (category === "document" || category === "queue") return "info";
  if (category === "frontend") return "warning";
  return "neutral";
}
